import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { normalizeE164 } from '@wrapforce/shared';
import { QUOTE_SERVICES, type QuoteService } from '@wrapforce/shared/constants';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input, Label, FieldError } from '@/components/ui/input';
import { useLocaleFormatters } from '@/lib/format';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { apiErrorMessage } from '@/lib/api-errors';
import { useDebounce } from '@/lib/useDebounce';
import {
  useConfirmDirectoryClaimEmailCode,
  useCreateDirectoryClaimRequest,
  useDirectoryListing,
  useDirectoryShopSearch,
  useSendDirectoryClaimEmailCode,
  useUpdateDirectoryListing,
  useUploadDirectoryListingMedia,
  type DirectoryHours,
  type DirectoryShopSearchResult,
} from './directory-api';
import { invalidFieldsFromApiError } from './form-errors';

// The public-facing directory site (carwrapshops.com) - no env var for it
// exists yet, so this mirrors the fallback pattern QuoteWidgetDetailPage
// uses for VITE_MARKETING_URL rather than adding one out of scope.
const DIRECTORY_URL = 'https://carwrapshops.com';

const DAYS: { key: string; labelKey: string }[] = [
  { key: 'mon', labelKey: 'directoryListing.days.mon' },
  { key: 'tue', labelKey: 'directoryListing.days.tue' },
  { key: 'wed', labelKey: 'directoryListing.days.wed' },
  { key: 'thu', labelKey: 'directoryListing.days.thu' },
  { key: 'fri', labelKey: 'directoryListing.days.fri' },
  { key: 'sat', labelKey: 'directoryListing.days.sat' },
  { key: 'sun', labelKey: 'directoryListing.days.sun' },
];

const ALLOWED_MEDIA_TYPES = ['image/jpeg', 'image/png'];
const MAX_MEDIA_BYTES = 3 * 1024 * 1024;

// §0089 - matches the server's ALLOWED_EVIDENCE_TYPES / MAX_EVIDENCE_BYTES
// (server/src/modules/directory-listing/service.ts) so a bad file is caught
// here instead of round-tripping to the server first.
const ALLOWED_EVIDENCE_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_EVIDENCE_BYTES = 10 * 1024 * 1024;

// A day's hours are stored as at most one [open, close] tuple - an empty (or
// missing) array means closed, exactly like directory_shops.hours and
// directory/src/pages/claim/[slug]/manage.astro.
function rangeFor(hours: DirectoryHours, day: string): [string, string] | null {
  return hours[day]?.[0] ?? null;
}

function setRangeFor(hours: DirectoryHours, day: string, range: [string, string] | null): DirectoryHours {
  return { ...hours, [day]: range ? [range] : [] };
}

export function DirectoryListingDetailPage() {
  const { t } = useTranslation('integrations');
  const { formatDateTime } = useLocaleFormatters();
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const { data, isLoading } = useDirectoryListing(workspaceId);
  const createClaim = useCreateDirectoryClaimRequest(workspaceId);
  const sendClaimEmailCode = useSendDirectoryClaimEmailCode(workspaceId);
  const confirmClaimEmailCode = useConfirmDirectoryClaimEmailCode(workspaceId);
  const update = useUpdateDirectoryListing(workspaceId);
  const uploadMedia = useUploadDirectoryListingMedia(workspaceId);

  // -------------------------------------------------------------------------
  // Not-claimed state: debounced search over /shops/search, then a short
  // claim-request form for the selected shop.
  // -------------------------------------------------------------------------
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const { data: searchResults, isFetching: searching } = useDirectoryShopSearch(workspaceId, debouncedSearch);
  const [claimTarget, setClaimTarget] = useState<DirectoryShopSearchResult | null>(null);
  const [claimBusinessEmail, setClaimBusinessEmail] = useState('');
  // Verification is a two-step affair: a code is emailed (claimCodeSent),
  // then confirmed (claimEmailVerified). Both reset the moment the address
  // itself changes, so a verified proof for one address can never be reused
  // to submit a claim for a different one - see handleClaimEmailChange.
  const [claimCodeSent, setClaimCodeSent] = useState(false);
  const [claimEmailVerified, setClaimEmailVerified] = useState(false);
  const [claimCode, setClaimCode] = useState('');
  const [claimEvidence, setClaimEvidence] = useState<File | null>(null);
  const [claimNote, setClaimNote] = useState('');
  const [claimErrors, setClaimErrors] = useState<{ businessEmail?: string; code?: string; evidence?: string }>({});
  const claimEvidenceInputRef = useRef<HTMLInputElement>(null);

  function resetClaimForm() {
    setClaimTarget(null);
    setClaimBusinessEmail('');
    setClaimCodeSent(false);
    setClaimEmailVerified(false);
    setClaimCode('');
    setClaimEvidence(null);
    setClaimNote('');
    setClaimErrors({});
  }

  // Any edit to the business email after a code was sent (or confirmed)
  // invalidates that verification - otherwise someone could verify one
  // address and quietly submit a claim for another.
  function handleClaimEmailChange(value: string) {
    setClaimBusinessEmail(value);
    setClaimErrors((prev) => ({ ...prev, businessEmail: undefined }));
    if (claimCodeSent || claimEmailVerified) {
      setClaimCodeSent(false);
      setClaimEmailVerified(false);
      setClaimCode('');
    }
  }

  async function handleSendClaimEmailCode() {
    const email = claimBusinessEmail.trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setClaimErrors((prev) => ({ ...prev, businessEmail: t('errors.emailInvalid') }));
      return;
    }
    try {
      await sendClaimEmailCode.mutateAsync(email);
      setClaimCodeSent(true);
      setClaimEmailVerified(false);
      setClaimCode('');
      toast.success(t('directoryListing.toast.claimCodeSent'));
    } catch (err) {
      toast.error(t('directoryListing.toast.claimCodeSendFailed'), { description: apiErrorMessage(err) });
    }
  }

  async function handleConfirmClaimEmailCode() {
    const code = claimCode.trim();
    if (!code) {
      setClaimErrors((prev) => ({ ...prev, code: t('directoryListing.claimCodeRequired') }));
      return;
    }
    try {
      await confirmClaimEmailCode.mutateAsync({ businessEmail: claimBusinessEmail.trim(), code });
      setClaimEmailVerified(true);
      setClaimErrors((prev) => ({ ...prev, code: undefined }));
      toast.success(t('directoryListing.toast.claimEmailVerified'));
    } catch (err) {
      setClaimErrors((prev) => ({ ...prev, code: apiErrorMessage(err) }));
    }
  }

  function handleClaimEvidenceFile(file: File | undefined) {
    if (!file) return;
    if (!ALLOWED_EVIDENCE_TYPES.includes(file.type) || file.size > MAX_EVIDENCE_BYTES) {
      setClaimErrors((prev) => ({ ...prev, evidence: t('directoryListing.claimEvidenceInvalid') }));
      return;
    }
    setClaimEvidence(file);
    setClaimErrors((prev) => ({ ...prev, evidence: undefined }));
  }

  async function handleSubmitClaim() {
    if (!claimTarget || !claimEmailVerified) return;
    const nextErrors: { businessEmail?: string; evidence?: string } = {};
    if (!/^\S+@\S+\.\S+$/.test(claimBusinessEmail.trim())) nextErrors.businessEmail = t('errors.emailInvalid');
    if (!claimEvidence) nextErrors.evidence = t('directoryListing.claimEvidenceRequired');
    if (Object.keys(nextErrors).length > 0) {
      setClaimErrors(nextErrors);
      return;
    }
    try {
      await createClaim.mutateAsync({
        shopId: claimTarget.id,
        businessEmail: claimBusinessEmail.trim(),
        evidence: claimEvidence!,
        note: claimNote.trim() || undefined,
      });
      toast.success(t('directoryListing.toast.claimSubmitted'));
      resetClaimForm();
    } catch (err) {
      const fields = invalidFieldsFromApiError(err);
      const mapped: { businessEmail?: string; evidence?: string } = {};
      if (fields.includes('businessEmail')) mapped.businessEmail = t('errors.emailInvalid');
      if (Object.keys(mapped).length > 0) {
        setClaimErrors(mapped);
      } else {
        toast.error(t('directoryListing.toast.claimSubmitFailed'), { description: apiErrorMessage(err) });
      }
    }
  }

  // -------------------------------------------------------------------------
  // Granted state: edit form for the fields the contract allows (services,
  // hours, email, phone, website) plus one-at-a-time logo/banner upload.
  // Name and address are read-only, sourced straight from the directory row.
  // -------------------------------------------------------------------------
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [services, setServices] = useState<QuoteService[]>([]);
  const [hours, setHours] = useState<DirectoryHours>({});
  const [errors, setErrors] = useState<{ email?: string; phone?: string; website?: string }>({});
  const [logoUploading, setLogoUploading] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (data?.listing) {
      setEmail(data.listing.email ?? '');
      setPhone(data.listing.phone ?? '');
      setWebsite(data.listing.website ?? '');
      setServices(data.listing.services);
      setHours(data.listing.hours);
    }
  }, [data]);

  function toggleService(service: QuoteService) {
    setServices((prev) => (prev.includes(service) ? prev.filter((s) => s !== service) : [...prev, service]));
  }

  function toggleClosed(day: string, closed: boolean) {
    setHours((prev) => setRangeFor(prev, day, closed ? null : ['09:00', '17:00']));
  }

  function setDayTime(day: string, index: 0 | 1, value: string) {
    setHours((prev) => {
      const current = rangeFor(prev, day) ?? ['09:00', '17:00'];
      const next: [string, string] = index === 0 ? [value, current[1]] : [current[0], value];
      return setRangeFor(prev, day, next);
    });
  }

  async function handleSave() {
    const nextErrors: { email?: string; phone?: string; website?: string } = {};
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) nextErrors.email = t('errors.emailInvalid');
    const normalizedPhone = normalizeE164(phone);
    if (!normalizedPhone) nextErrors.phone = t('errors.phoneInvalid');
    let websiteValid = false;
    try {
      websiteValid = new URL(website).protocol === 'https:';
    } catch {
      websiteValid = false;
    }
    if (!websiteValid) nextErrors.website = t('errors.websiteInvalid');
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    try {
      await update.mutateAsync({ email: email.trim(), phone: normalizedPhone!, website: website.trim(), services, hours });
      toast.success(t('directoryListing.toast.saved'));
    } catch (err) {
      const fields = invalidFieldsFromApiError(err);
      const mapped: { email?: string; phone?: string; website?: string } = {};
      if (fields.includes('email')) mapped.email = t('errors.emailInvalid');
      if (fields.includes('phone')) mapped.phone = t('errors.phoneInvalid');
      if (fields.includes('website')) mapped.website = t('errors.websiteInvalid');
      if (Object.keys(mapped).length > 0) {
        setErrors(mapped);
      } else {
        toast.error(t('directoryListing.toast.saveFailed'), { description: apiErrorMessage(err) });
      }
    }
  }

  async function handleMediaFile(field: 'logo' | 'banner', file: File | undefined) {
    if (!file) return;
    if (!ALLOWED_MEDIA_TYPES.includes(file.type) || file.size > MAX_MEDIA_BYTES) {
      toast.error(t('directoryListing.toast.mediaInvalid'));
      return;
    }
    const setUploading = field === 'logo' ? setLogoUploading : setBannerUploading;
    setUploading(true);
    try {
      await uploadMedia.mutateAsync({ field, file });
      toast.success(field === 'logo' ? t('directoryListing.toast.logoUploaded') : t('directoryListing.toast.bannerUploaded'));
    } catch (err) {
      toast.error(
        field === 'logo' ? t('directoryListing.toast.logoUploadFailed') : t('directoryListing.toast.bannerUploadFailed'),
        { description: apiErrorMessage(err) },
      );
    } finally {
      setUploading(false);
    }
  }

  const listing = data?.listing ?? null;
  const pendingRequest = data?.pendingRequest ?? null;
  const publicListingUrl = listing ? `${DIRECTORY_URL}/shop/${listing.slug}` : null;
  const pendingListingUrl = pendingRequest?.shopSlug ? `${DIRECTORY_URL}/shop/${pendingRequest.shopSlug}` : null;

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <Link to="/integrations" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink">
          <ArrowLeft size={14} />
          {t('directoryListing.back')}
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="font-display text-xl font-bold text-ink">{t('directoryListing.title')}</h1>
          {listing ? (
            <a
              href={publicListingUrl!}
              target="_blank"
              rel="noreferrer"
              className="ml-auto inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
            >
              <ExternalLink size={14} />
              {t('directoryListing.viewPublicListing')}
            </a>
          ) : null}
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted">{t('common:actions.loading')}</p>
      ) : listing ? (
        <div className="flex max-w-2xl flex-col gap-4">
          <Card>
            <CardContent className="flex flex-col gap-1 bg-surface-2">
              <p className="text-sm font-bold text-ink">{listing.name}</p>
              <p className="text-xs text-muted">
                {listing.city}, {listing.region}
              </p>
              <p className="mt-2 text-xs text-muted">{t('directoryListing.fixedFieldsNote')}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('directoryListing.grantedTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="directory-listing-email">{t('directoryListing.quoteContactEmail')}</Label>
                <Input
                  id="directory-listing-email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrors((prev) => ({ ...prev, email: undefined }));
                  }}
                />
                <FieldError>{errors.email}</FieldError>
                <p className="text-xs text-muted">{t('directoryListing.quoteContactEmailHint')}</p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="directory-listing-phone">{t('directoryListing.storePhone')}</Label>
                  <Input
                    id="directory-listing-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      setErrors((prev) => ({ ...prev, phone: undefined }));
                    }}
                  />
                  <FieldError>{errors.phone}</FieldError>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="directory-listing-website">{t('directoryListing.website')}</Label>
                  <Input
                    id="directory-listing-website"
                    type="url"
                    value={website}
                    onChange={(e) => {
                      setWebsite(e.target.value);
                      setErrors((prev) => ({ ...prev, website: undefined }));
                    }}
                  />
                  <FieldError>{errors.website}</FieldError>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label>{t('directoryListing.servicesOffered')}</Label>
                <div className="flex flex-wrap gap-3">
                  {QUOTE_SERVICES.map((service) => (
                    <label key={service} className="flex items-center gap-2 border border-border px-3 py-2 text-sm text-ink">
                      <input type="checkbox" checked={services.includes(service)} onChange={() => toggleService(service)} />
                      {t(`directoryListing.services.${service}`)}
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label>{t('directoryListing.hours')}</Label>
                <div className="flex flex-col gap-2">
                  {DAYS.map((day) => {
                    const range = rangeFor(hours, day.key);
                    const closed = !range;
                    return (
                      <div key={day.key} className="flex flex-wrap items-center gap-3">
                        <span className="w-24 text-sm font-medium text-ink">{t(day.labelKey)}</span>
                        <label className="flex items-center gap-2 text-sm text-muted">
                          <input type="checkbox" checked={closed} onChange={(e) => toggleClosed(day.key, e.target.checked)} />
                          {t('directoryListing.hoursClosed')}
                        </label>
                        <input
                          type="time"
                          aria-label={t('directoryListing.hoursOpenAt', { day: t(day.labelKey) })}
                          value={range?.[0] ?? ''}
                          disabled={closed}
                          onChange={(e) => setDayTime(day.key, 0, e.target.value)}
                          className="h-9 border border-border bg-surface px-2 text-sm text-ink outline-none disabled:opacity-40"
                        />
                        <span className="text-muted">{t('directoryListing.hoursTo')}</span>
                        <input
                          type="time"
                          aria-label={t('directoryListing.hoursCloseAt', { day: t(day.labelKey) })}
                          value={range?.[1] ?? ''}
                          disabled={closed}
                          onChange={(e) => setDayTime(day.key, 1, e.target.value)}
                          className="h-9 border border-border bg-surface px-2 text-sm text-ink outline-none disabled:opacity-40"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              <Button className="w-fit" loading={update.isPending} onClick={handleSave}>
                {t('common:actions.save')}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('directoryListing.logo')}</CardTitle>
            </CardHeader>
            <CardContent className="flex items-center gap-4">
              {listing.logoUrl ? (
                <img src={listing.logoUrl} alt={t('directoryListing.logo')} className="h-16 w-16 border border-border bg-surface object-contain" />
              ) : (
                <div className="grid h-16 w-16 place-items-center border border-dashed border-border bg-surface-2 text-center text-xs text-muted">
                  {t('directoryListing.noLogo')}
                </div>
              )}
              <div className="flex flex-col items-start gap-2">
                <Button variant="outline" size="sm" loading={logoUploading} onClick={() => logoInputRef.current?.click()}>
                  {listing.logoUrl ? t('directoryListing.replaceLogo') : t('directoryListing.uploadLogo')}
                </Button>
                <p className="text-xs text-muted">{t('directoryListing.logoHint')}</p>
              </div>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  void handleMediaFile('logo', file);
                }}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('directoryListing.banner')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {listing.bannerUrl ? (
                <img src={listing.bannerUrl} alt={t('directoryListing.banner')} className="h-24 w-full border border-border bg-surface object-cover" />
              ) : (
                <div className="grid h-24 w-full place-items-center border border-dashed border-border bg-surface-2 text-center text-xs text-muted">
                  {t('directoryListing.noBanner')}
                </div>
              )}
              <div className="flex flex-col items-start gap-2">
                <Button variant="outline" size="sm" loading={bannerUploading} onClick={() => bannerInputRef.current?.click()}>
                  {listing.bannerUrl ? t('directoryListing.replaceBanner') : t('directoryListing.uploadBanner')}
                </Button>
                <p className="text-xs text-muted">{t('directoryListing.bannerHint')}</p>
              </div>
              <input
                ref={bannerInputRef}
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  void handleMediaFile('banner', file);
                }}
              />
            </CardContent>
          </Card>
        </div>
      ) : pendingRequest ? (
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>{t('directoryListing.pendingTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <p className="text-sm text-ink">{t('directoryListing.pendingBody', { shopName: pendingRequest.shopName })}</p>
            <p className="text-xs text-muted">
              {t('directoryListing.pendingSubmittedAt', { date: formatDateTime(new Date(pendingRequest.createdAt)) })}
            </p>
            {pendingListingUrl ? (
              <a
                href={pendingListingUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex w-fit items-center gap-1.5 text-sm text-accent hover:underline"
              >
                <ExternalLink size={14} />
                {t('directoryListing.viewPublicListing')}
              </a>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <div className="flex max-w-xl flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('directoryListing.explainerTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <p className="text-sm text-muted">{t('directoryListing.explainerBody')}</p>
              <div className="flex flex-col gap-2">
                <Label htmlFor="directory-listing-search">{t('directoryListing.searchLabel')}</Label>
                <Input
                  id="directory-listing-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('directoryListing.searchPlaceholder')}
                  aria-label={t('directoryListing.searchLabel')}
                />
                <p className="text-xs text-muted">{t('directoryListing.searchHint')}</p>
              </div>
              {debouncedSearch.trim().length >= 3 ? (
                <div className="flex flex-col divide-y divide-border border border-border">
                  {searching ? (
                    <p className="p-3 text-sm text-muted">{t('common:actions.loading')}</p>
                  ) : searchResults?.items.length ? (
                    searchResults.items.map((result) => (
                      <div key={result.id} className="flex items-center justify-between gap-3 p-3">
                        <div>
                          <p className="text-sm font-bold text-ink">{result.name}</p>
                          <p className="text-xs text-muted">
                            {result.city}, {result.region}
                          </p>
                        </div>
                        {result.owned ? (
                          <span className="shrink-0 bg-surface-2 px-2.5 py-1 text-xs font-bold text-muted">{t('directoryListing.resultOwned')}</span>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => setClaimTarget(result)}>
                            {t('directoryListing.resultSelect')}
                          </Button>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="p-3 text-sm text-muted">{t('directoryListing.searchNoResults')}</p>
                  )}
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      )}

      {claimTarget ? (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) resetClaimForm();
          }}
          title={t('directoryListing.claimDialogTitle', { shopName: claimTarget.name })}
          description={t('directoryListing.claimDialogDescription')}
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="directory-claim-business-email">{t('directoryListing.claimBusinessEmailLabel')}</Label>
              <div className="flex gap-2">
                <Input
                  id="directory-claim-business-email"
                  type="email"
                  value={claimBusinessEmail}
                  onChange={(e) => handleClaimEmailChange(e.target.value)}
                  placeholder={t('directoryListing.claimBusinessEmailPlaceholder')}
                  disabled={claimEmailVerified}
                  className="flex-1"
                />
                {!claimEmailVerified ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-fit shrink-0"
                    loading={sendClaimEmailCode.isPending}
                    onClick={handleSendClaimEmailCode}
                  >
                    {claimCodeSent ? t('directoryListing.claimBusinessEmailResendCode') : t('directoryListing.claimBusinessEmailSendCode')}
                  </Button>
                ) : null}
              </div>
              <FieldError>{claimErrors.businessEmail}</FieldError>
              {claimEmailVerified ? (
                <p className="text-xs font-bold text-success">{t('directoryListing.claimEmailVerifiedLabel')}</p>
              ) : (
                <p className="text-xs text-muted">{t('directoryListing.claimBusinessEmailHint')}</p>
              )}
            </div>

            {claimCodeSent && !claimEmailVerified ? (
              <div className="flex flex-col gap-2">
                <Label htmlFor="directory-claim-code">{t('directoryListing.claimCodeLabel')}</Label>
                <div className="flex gap-2">
                  <Input
                    id="directory-claim-code"
                    inputMode="numeric"
                    value={claimCode}
                    onChange={(e) => {
                      setClaimCode(e.target.value);
                      setClaimErrors((prev) => ({ ...prev, code: undefined }));
                    }}
                    placeholder={t('directoryListing.claimCodePlaceholder')}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-fit shrink-0"
                    loading={confirmClaimEmailCode.isPending}
                    onClick={handleConfirmClaimEmailCode}
                  >
                    {t('directoryListing.claimCodeVerifyButton')}
                  </Button>
                </div>
                <FieldError>{claimErrors.code}</FieldError>
                <p className="text-xs text-muted">{t('directoryListing.claimCodeHint', { email: claimBusinessEmail.trim() })}</p>
              </div>
            ) : null}

            {claimEmailVerified ? (
              <>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="directory-claim-evidence">{t('directoryListing.claimEvidenceLabel')}</Label>
                  <Button
                    id="directory-claim-evidence"
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-fit"
                    onClick={() => claimEvidenceInputRef.current?.click()}
                  >
                    {claimEvidence ? claimEvidence.name : t('directoryListing.claimEvidenceChoose')}
                  </Button>
                  <input
                    ref={claimEvidenceInputRef}
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      handleClaimEvidenceFile(file);
                    }}
                  />
                  <FieldError>{claimErrors.evidence}</FieldError>
                  <p className="text-xs text-muted">{t('directoryListing.claimEvidenceHint')}</p>
                  <p className="text-xs text-muted">{t('directoryListing.claimEvidencePrivacyNote')}</p>
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="directory-claim-note">{t('directoryListing.claimNoteLabel')}</Label>
                  <textarea
                    id="directory-claim-note"
                    value={claimNote}
                    onChange={(e) => setClaimNote(e.target.value)}
                    placeholder={t('directoryListing.claimNotePlaceholder')}
                    rows={3}
                    className="w-full border border-border bg-surface px-3.5 py-2 text-sm text-ink outline-none placeholder:text-muted focus-visible:border-accent"
                  />
                </div>
              </>
            ) : (
              <p className="text-xs text-muted">{t('directoryListing.claimVerifyEmailFirstHint')}</p>
            )}

            <Button className="w-fit" loading={createClaim.isPending} disabled={!claimEmailVerified} onClick={handleSubmitClaim}>
              {t('directoryListing.claimSubmit')}
            </Button>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
